<template>
  <div class="pos-panel">
    <button class="pos-back" type="button" @click="goBack">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
      Voltar
    </button>

    <div v-if="loading" class="pos-loading">Carregando...</div>
    <div v-else-if="loadError" class="pos-error">{{ loadError }}</div>

    <template v-else-if="os">
      <div class="pos-header">
        <div class="pos-title">OS{{ os.osNumber }} — {{ os.title }}</div>
        <div class="pos-meta">
          {{ os.projectName }} · {{ os.responsaveis.join(', ') }}
          <span v-if="os.dueDate"> · prazo {{ formatDate(os.dueDate) }}</span>
          <span class="pos-priority-badge" :class="'p-' + os.priority">{{ priorityLabel(os.priority) }}</span>
        </div>
        <p v-if="os.description" class="pos-desc">{{ os.description }}</p>
      </div>

      <div v-if="pendingItems.length === 0" class="pos-empty">Todas as rotinas desta OS já foram preenchidas.</div>

      <div v-else class="pos-items">
        <div v-for="item in pendingItems" :key="item.id" class="pos-item">
          <div class="pos-item-title">{{ item.title }}</div>
          <p v-if="item.description" class="pos-item-desc">{{ item.description }}</p>
          <textarea class="pos-textarea" v-model="evidenceFor(item.id).textResponse" rows="2" placeholder="Descreva o que foi feito (opcional)"></textarea>
          <label class="pos-check-row">
            <input type="checkbox" v-model="evidenceFor(item.id).checkResponse" />
            Marcar como concluído
          </label>
          <input type="file" accept="image/*" @change="onPhotoSelected($event, item.id)" />
        </div>
      </div>

      <p v-if="submitError" class="pos-error">{{ submitError }}</p>
      <div class="pos-actions">
        <button v-if="pendingItems.length > 0" class="pos-btn pos-btn-primary" type="button" :disabled="saving" @click="submitAll">
          {{ saving ? 'Enviando...' : `Confirmar conclusão (${pendingItems.length} ${pendingItems.length > 1 ? 'itens' : 'item'})` }}
        </button>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { maintenanceStore, type ServiceOrderDetail } from '@/store/maintenance'

export default defineComponent({
  name: 'PreencherOSPanel',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const loading = ref(true)
    const loadError = ref('')
    const saving = ref(false)
    const submitError = ref('')
    const os = ref<ServiceOrderDetail | null>(null)
    const evidenceMap = reactive<Record<string, { textResponse: string; checkResponse: boolean; photoResponse: string }>>({})

    const osId = computed(() => String(route.params.osId || ''))
    const pendingItems = computed(() => (os.value?.items || []).filter(it => it.status === 'pending'))

    const priorityLabel = (p: string) => ({ baixa: 'Baixa', normal: 'Normal', alta: 'Alta', urgente: 'Urgente' }[p] || 'Normal')
    const formatDate = (iso: string) => { if (!iso) return ''; const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}` }

    const evidenceFor = (itemId: string) => {
      if (!evidenceMap[itemId]) {
        evidenceMap[itemId] = { textResponse: '', checkResponse: false, photoResponse: '' }
      }
      return evidenceMap[itemId]
    }

    const onPhotoSelected = (event: Event, itemId: string) => {
      const file = (event.target as HTMLInputElement).files?.[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = () => { evidenceFor(itemId).photoResponse = String(reader.result || '') }
      reader.readAsDataURL(file)
    }

    const goBack = () => {
      if (window.history.length > 1) router.back()
      else router.push('/home')
    }

    const submitAll = async () => {
      submitError.value = ''
      saving.value = true
      try {
        for (const item of pendingItems.value) {
          const ev = evidenceFor(item.id)
          await maintenanceStore.completeServiceOrderItem(item.id, {
            textResponse: ev.textResponse.trim(),
            checkResponse: ev.checkResponse,
            photoResponse: ev.photoResponse
          })
        }
        await maintenanceStore.fetchMyTasks()
        goBack()
      } catch (err) {
        submitError.value = err instanceof Error ? err.message : 'Erro ao concluir itens.'
      } finally {
        saving.value = false
      }
    }

    onMounted(async () => {
      try {
        os.value = await maintenanceStore.loadServiceOrderDetail(osId.value)
      } catch (err) {
        loadError.value = err instanceof Error ? err.message : 'Erro ao carregar a OS.'
      } finally {
        loading.value = false
      }
    })

    return {
      loading, loadError, saving, submitError, os, pendingItems,
      priorityLabel, formatDate, evidenceFor, onPhotoSelected, goBack, submitAll
    }
  }
})
</script>

<style scoped lang="scss">
.pos-panel { width: 100%; height: 100%; overflow-y: auto; padding: 18px 22px 32px; display: flex; flex-direction: column; gap: 16px; }

.pos-back { display: flex; align-items: center; gap: 4px; background: none; border: none; color: var(--ink-muted); font-size: 13px; cursor: pointer; padding: 4px 6px; align-self: flex-start; }

.pos-loading, .pos-empty { font-size: 13px; color: var(--ink-muted); padding: 24px 0; text-align: center; }
.pos-error { color: #dc2626; font-size: 13px; margin: 0; }

.pos-header { display: flex; flex-direction: column; gap: 6px; }
.pos-title { font-size: 20px; font-weight: 700; color: var(--ink); }
.pos-meta { font-size: 13px; color: var(--ink-muted); display: flex; align-items: center; flex-wrap: wrap; gap: 4px; }
.pos-desc { font-size: 13px; color: var(--ink); margin: 4px 0 0; }

.pos-priority-badge {
  display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 700; margin-left: 6px;
  &.p-baixa { background: rgba(100,116,139,.12); color: #475569; }
  &.p-normal { background: rgba(37,99,235,.12); color: #2563eb; }
  &.p-alta { background: rgba(217,119,6,.12); color: #d97706; }
  &.p-urgente { background: rgba(220,38,38,.12); color: #dc2626; }
}

.pos-items { display: flex; flex-direction: column; gap: 14px; }
.pos-item {
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.pos-item-title { font-size: 14px; font-weight: 700; color: var(--ink); }
.pos-item-desc { font-size: 12px; color: var(--ink-muted); margin: 0; }

.pos-textarea {
  border: 1px solid rgba(31,30,26,.18);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
}
.pos-check-row { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--ink); }

.pos-actions { display: flex; justify-content: flex-end; }
.pos-btn {
  border: none; border-radius: 10px; padding: 12px 24px; font-size: 14px; font-weight: 700; cursor: pointer;
  background: rgba(31,30,26,.08); color: var(--ink);
  &:disabled { opacity: .6; cursor: default; }
}
.pos-btn-primary { background: var(--accent); color: #fff; }
</style>
